import {
  BookOpenIcon,
  MessageCircleQuestionIcon,
  HourglassIcon,
  HeartIcon,
  SparklesIcon,
  HandHeartIcon,
  SmartphoneIcon,
  BookmarkIcon,
  CalendarDaysIcon,
  NetworkIcon,
  BookHeartIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const checkoutUrl = 'https://el-poder-de-los-salmos.impultienda.ar/checkout';

export const coverImage =
  'https://cdn.magicpatterns.com/uploads/dUA7X6MFPs4vWRGK1hsvbD/Gemini_Generated_Image_vt46zrvt46zrvt46.jpg';

export type IconItem = { icon: LucideIcon; title: string; text: string };

export const painPoints: IconItem[] = [
  {
    icon: BookOpenIcon,
    title: 'Querés comprender mejor la Biblia',
    text: 'Leés los Salmos, pero sentís que necesitás profundizar en sus mensajes y encontrarles un significado más claro para tu vida cotidiana.',
  },
  {
    icon: MessageCircleQuestionIcon,
    title: 'Buscás respuestas en momentos difíciles',
    text: 'Hay días en los que necesitás una palabra de esperanza, fortaleza o consuelo para seguir adelante.',
  },
  {
    icon: HourglassIcon,
    title: 'Te cuesta mantener una rutina espiritual',
    text: 'Querés dedicar más tiempo a la oración y a la lectura bíblica, pero muchas veces las responsabilidades diarias ocupan todo tu tiempo.',
  },
  {
    icon: HeartIcon,
    title: 'Sentís que necesitás fortalecer tu fe',
    text: 'Querés construir una relación más consciente con Dios y encontrar momentos de reflexión que te ayuden a vivir tu espiritualidad con mayor intención.',
  },
];

export const features: IconItem[] = [
  {
    icon: BookOpenIcon,
    title: 'Una lectura para profundizar en tu fe',
    text: 'Acercate a los Salmos con una mirada reflexiva y descubrí nuevas formas de relacionar sus enseñanzas con tu vida cotidiana.',
  },
  {
    icon: SparklesIcon,
    title: 'Inspiración para distintos momentos de la vida',
    text: 'Encontrá en las Escrituras palabras que invitan a reflexionar sobre la esperanza, la confianza, la gratitud, el arrepentimiento y la fortaleza espiritual.',
  },
  {
    icon: HandHeartIcon,
    title: 'Un espacio para la oración y la reflexión',
    text: 'Convertí unos minutos de lectura en un momento para detenerte, agradecer, expresar tus preocupaciones y renovar tu intención de caminar con Dios.',
  },
  {
    icon: SmartphoneIcon,
    title: 'A tu ritmo y desde cualquier lugar',
    text: 'Leé desde tu celular, tablet o computadora, en casa, antes de dormir o en el momento del día que prefieras.',
  },
  {
    icon: BookmarkIcon,
    title: 'Un recurso para volver a consultar',
    text: 'Tenelo siempre disponible para acompañar tus momentos de estudio bíblico y reflexión personal.',
  },
];

export type Bonus = {
  label: string;
  title: string;
  tagline: string;
  text: string;
  idealFor: string;
  image: string;
};

export const bonuses: Bonus[] = [
  {
    label: 'Bono 1',
    title: '365 Días Caminando con Dios',
    tagline: 'Un año de inspiración para acompañar tu camino de fe.',
    text: 'Un recurso pensado para ayudarte a incorporar momentos de reflexión espiritual a tu rutina, mantener presente tu relación con Dios y dedicar cada día un espacio a tu crecimiento interior.',
    idealFor: 'Ideal para quienes desean vivir su fe con mayor constancia y propósito.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/87d8b087-25dd-4b05-b772-c489febfe949.jpg',
  },
  {
    label: 'Bono 2',
    title: '66 Mapas Mentales de la Biblia',
    tagline: 'Visualizá las Escrituras de una manera más organizada.',
    text: 'Un recurso visual para explorar los libros y los principales temas bíblicos de forma esquemática, facilitando el repaso, el estudio y la comprensión de las conexiones entre diferentes enseñanzas.',
    idealFor: 'Ideal para acompañar tu lectura bíblica y estudiar de una manera más visual.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/864f8b18-8449-4ff0-a3f7-22d06d28992d.jpg',
  },
  {
    label: 'Bono 3',
    title: 'Planner de Amor Propio',
    tagline: 'Reservá un espacio para cuidar tu interior.',
    text: 'Una herramienta de planificación y reflexión personal para organizar tus objetivos, registrar tus pensamientos y dedicar tiempo a tu bienestar emocional.',
    idealFor: 'Porque cuidar tu mundo interior también puede formar parte de un camino de crecimiento personal y espiritual.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/023a187c-5fff-4324-99a8-a4fcf2ec2f4f.jpg',
  },
];

export const packItems: IconItem[] = [
  {
    icon: BookHeartIcon,
    title: 'El Poder de los Salmos',
    text: 'Tu ebook cristiano principal para acompañar tu lectura y reflexión bíblica (150 salmos).',
  },
  {
    icon: CalendarDaysIcon,
    title: '365 Días Caminando con Dios',
    text: 'Un recurso para incorporar momentos de inspiración espiritual durante todo el año.',
  },
  {
    icon: NetworkIcon,
    title: '66 Mapas Mentales de la Biblia',
    text: 'Una herramienta visual para organizar y repasar los contenidos bíblicos.',
  },
  {
    icon: HeartIcon,
    title: 'Planner de Amor Propio',
    text: 'Un espacio para organizar tus objetivos y dedicar tiempo a tu bienestar personal.',
  },
];

export const pricingIncludes: string[] = [
  'Ebook cristiano digital (150 salmos)',
  'Bono 1: 365 Días Caminando con Dios',
  'Bono 2: 66 Mapas Mentales de la Biblia',
  'Bono 3: Planner de Amor Propio',
  'Acceso digital inmediato después de confirmar el pago',
  'Consultalo cuando quieras',
];

export type Faq = { question: string; answer: string };

export const faqs: Faq[] = [
  {
    question: '¿Qué es El Poder de los Salmos?',
    answer:
      'Es un ebook cristiano digital pensado para acompañarte en la lectura, la reflexión bíblica y el crecimiento espiritual.',
  },
  {
    question: '¿Qué incluye mi compra?',
    answer:
      'Recibís el ebook principal y los tres bonos: 365 Días Caminando con Dios, 66 Mapas Mentales de la Biblia y Planner de Amor Propio.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Una vez confirmado el pago, vas a recibir las instrucciones de acceso o descarga por el medio indicado en la compra.',
  },
  {
    question: '¿Puedo leerlo desde mi celular?',
    answer:
      'Sí, siempre que el archivo se entregue en un formato compatible con tu dispositivo. Podés consultarlo desde tu celular, tablet o computadora.',
  },
  {
    question: '¿Tengo que leerlo todo de una vez?',
    answer: 'No. Podés avanzar a tu propio ritmo y volver a consultar los materiales cuando lo necesites.',
  },
  {
    question: '¿Cuánto cuesta el pack completo?',
    answer:
      'El precio de lanzamiento es de US$14,99 por única vez, e incluye el ebook principal y los tres bonos digitales.',
  },
  {
    question: '¿Es una compra segura?',
    answer:
      'El pago se procesa mediante los medios de pago habilitados en la página de compra. Antes de pagar, verificá que estés en el sitio oficial y que los datos de la operación sean correctos.',
  },
  {
    question: '¿Qué pasa si no estoy conforme con mi compra?',
    answer: 'Consultá las condiciones de devolución publicadas en la página de compra.',
  },
];
